'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { supabase } from '@/lib/supabase'
import { sichererRuecksprung } from '@/lib/ruecksprung'
import { T, detectLang, type Lang } from '../landingCopy'

export default function LoginCard() {
  const router = useRouter()
  const [lang, setLang] = useState<Lang>('DE')
  const t = T[lang]

  // Detect browser language on mount — DE for German browsers, EN otherwise.
  useEffect(() => { setLang(detectLang()) }, [])

  /**
   * Zurück, wo man war — nicht pauschal aufs Dashboard.
   *
   * Die Middleware reicht bei einer Umleitung auf die Anmeldung ein `next`
   * durch (z. B. `/news`). Wurde es ignoriert, landete jeder auf der
   * Startseite: wer beim Lesen kurz die Sitzung erneuern musste, verlor
   * seine Stelle. `sichererRuecksprung` prüft den Pfad, damit daraus keine
   * offene Weiterleitung wird.
   *
   * Ohne Team geht es weiter zur Einrichtung — dort ist die Reihenfolge
   * wichtiger als das Ziel, aus dem man kam.
   */
  async function redirectBasedOnTeam() {
    const next = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('next')
      : null
    try {
      const r = await fetch('/api/teams')
      const d = r.ok ? await r.json() : null
      router.replace(d?.team ? sichererRuecksprung(next) : '/onboarding')
    } catch { router.replace(sichererRuecksprung(next)) }
  }
  // Password-recovery links must reach /auth/reset. If Supabase falls back to
  // the Site URL (e.g. the reset URL isn't allow-listed), the recovery hash
  // lands here instead. Capture it synchronously on first render — before the
  // Supabase client's async detectSessionInUrl consumes and strips the hash —
  // so we can forward it rather than let the session bounce into /dashboard.
  const [recoveryHash] = useState(() =>
    typeof window !== 'undefined' && /(?:type=recovery|error_code=)/.test(window.location.hash)
      ? window.location.hash
      : null,
  )
  useEffect(() => {
    let alive = true
    if (recoveryHash) { router.replace('/auth/reset' + recoveryHash); return }
    supabase.auth.getSession().then(({ data: { session } }: any) => {
      if (session && alive) redirectBasedOnTeam()
    })
    const { data: sub } = supabase.auth.onAuthStateChange((event: any, session: any) => {
      // A recovery session is NOT a normal login — never send it to the app.
      if (event === 'PASSWORD_RECOVERY') { router.replace('/auth/reset'); return }
      if (session) redirectBasedOnTeam()
    })
    return () => { alive = false; sub?.unsubscribe?.() }
  }, [])

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password.trim()) return
    setLoading(true); setErr('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setErr(t.loginError)
    setLoading(false)
  }

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div className="fx-eyebrow" style={{ marginBottom: '0.5rem' }}>{t.loginSub}</div>
        <h2 style={{ fontSize: '1.875rem', fontWeight: 700, margin: 0, letterSpacing: '-0.015em', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <span aria-hidden style={{ fontSize: '1.375rem' }}>🔐</span>
          {t.loginTitle}
        </h2>
      </div>

      {err && (
        <div role="alert" style={{ padding: '0.9rem 1.1rem', borderRadius: 10, background: 'rgba(220,38,38,0.08)', border: '1.5px solid var(--color-loss)', color: 'var(--color-loss)', fontSize: '1.0625rem', fontWeight: 600, marginBottom: '1rem' }}>{err}</div>
      )}

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem' }}>
        <div>
          <label htmlFor="fx-email" className="fx-label">{t.emailLabel}</label>
          <input id="fx-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.emailPlaceholder} required autoComplete="email" className="fx-input" />
        </div>
        <div>
          <label htmlFor="fx-password" className="fx-label">{t.passwordLabel}</label>
          <input id="fx-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t.passwordPlaceholder} required autoComplete="current-password" className="fx-input" />
        </div>
        <button type="submit" disabled={loading} className="fx-btn-primary" style={{ opacity: loading ? 0.6 : 1, marginTop: '0.25rem' }}>{loading ? t.loggingIn : t.login}</button>
        <a href="/auth/forgot" style={{ alignSelf: 'flex-end', fontSize: '1.0625rem', fontWeight: 600, color: 'var(--color-accent)', textDecoration: 'underline', textUnderlineOffset: 3, marginTop: '0.5rem' }}>{t.forgotLink}</a>
      </form>

      <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <span aria-hidden style={{ fontSize: '0.875rem' }}>🔒</span>
            <span>{t.secTls}</span>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <span aria-hidden style={{ fontSize: '0.875rem' }}>🛡️</span>
            <span>{t.sec2fa}</span>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <span aria-hidden style={{ fontSize: '0.875rem' }}>🇨🇭</span>
            <span>{t.secHost}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
